/**
 * Block B Phase 2.D (2026-06-01) — Migration Wizard API client.
 *
 * 5 endpoints:
 *   POST   /migration/runs
 *   GET    /migration/runs
 *   GET    /migration/runs/{id}
 *   PATCH  /migration/runs/{id}
 *   DELETE /migration/runs/{id}
 *   POST   /migration/runs/{id}/start
 */
import { api } from './client';

export type MigrationProfile =
  | 'small_business'
  | 'medium_business'
  | 'enterprise'
  | 'custom';

export type MigrationStatus =
  | 'WIZARD_IN_PROGRESS'
  | 'READY'
  | 'IMPORTING'
  | 'BULK_POSTING'
  | 'VALIDATING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ROLLED_BACK';

export interface MigrationDepthConfig {
  payroll_months: number;
  fifo_months: number;
  vat_months: number;
  tax_invoices_months: number;
  cash_bank_months: number;
  goods_months: number;
  advance_reports_months: number;
  commission_months: number;
  hr_orders_months: number;
  assets_snapshot_only: boolean;
  assets_audit_months: number;
  orders_active_only: boolean;
}

// Phase-3.5 (2026-06-01): 5 канонічних 1С/BAS-конфігурацій.
// Дзеркало `SourceConfig` у backend schemas/migration_run.py.
export type SourceConfig =
  | 'utp'
  | 'generic'
  | '1c_bp_ua_2x'
  | 'bas_bukh'
  | 'ut_ua';

// Block C Порція 1 (2026-06-01): структура reconciliation findings.
// Дзеркало `ReconciliationFinding` у backend reconciliation_engine.py.
export type ReconciliationSource =
  | 'production_calendar'
  | 'accounting_constants'
  | 'period_date';

export interface ReconciliationFinding {
  source: ReconciliationSource;
  field: string;
  our_value: unknown;
  their_value: unknown;
  difference_amount: string;  // Decimal as string
  context: string;
  recommendation: string;
  document_external_id: string | null;
  target_table: string | null;
}

export interface ReconciliationReport {
  computed_at: string;
  findings: ReconciliationFinding[];
  summary: {
    total_findings: number;
    estimated_impact_uah: string;  // Decimal as string
    blocking_findings: number;
  };
  // Звірка ОСВ (2026-07-19): еталонні обороти Дт×Кт з регістра
  // бухгалтерії 1С vs наші acc.entries (секція osv_1c обробки).
  osv?: OsvReconciliation;
}

export interface OsvAccountRow {
  code: string;
  dt_1c: string;
  dt_ours: string;
  diff_dt: string;
  kt_1c: string;
  kt_ours: string;
  diff_kt: string;
  ok: boolean;
}

export interface OsvPairRow {
  dt: string;
  kt: string;
  amount_1c: string;
  amount_ours: string;
  diff: string;
}

export interface OsvReconciliation {
  error?: string;
  computed_at?: string;
  period?: { from: string; to: string };
  summary?: {
    total_1c: string;
    total_ours: string;
    total_diff: string;
    rows_1c: number;
    accounts_total: number;
    accounts_ok: number;
    accounts_diff: number;
  };
  accounts?: OsvAccountRow[];
  pairs?: OsvPairRow[];
  pairs_truncated?: number;
  months?: { y: number; m: number; abs_diff: string }[];
}

export interface MigrationRun {
  id: string;
  tenant_id: string;
  organization_id: string;
  created_by: string;
  migration_date: string;
  profile: MigrationProfile | string;
  depth_config: MigrationDepthConfig;
  source_config: SourceConfig | null;
  status: MigrationStatus | string;
  progress_percent: number;
  progress_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  imported_sections: Record<string, unknown> | null;
  validation_report: Record<string, unknown> | null;
  error_report: Record<string, unknown> | null;
  reverse_export_available_until: string | null;
  // Block C Порція 1: reconciliation findings 3 джерел розбіжностей.
  // NULL для legacy runs до Block C.
  reconciliation_report: ReconciliationReport | null;
  created_at: string;
  updated_at: string;
}

export interface MigrationRunListItem {
  id: string;
  organization_id: string;
  migration_date: string;
  profile: string;
  source_config: SourceConfig | null;
  status: string;
  progress_percent: number;
  created_at: string;
  completed_at: string | null;
}

export interface MigrationRunCreate {
  organization_id: string;
  migration_date: string;
  profile: MigrationProfile;
  depth_config?: MigrationDepthConfig | null;
}

export interface MigrationRunUpdate {
  migration_date?: string;
  profile?: MigrationProfile;
  depth_config?: MigrationDepthConfig;
}

// Profile defaults — dublikate Pydantic для frontend wizard step.
// Phase 1 Review Decisions (власник 2026-06-01).
export const PROFILE_DEFAULTS: Record<
  Exclude<MigrationProfile, 'custom'>,
  MigrationDepthConfig
> = {
  small_business: {
    payroll_months: 12,
    fifo_months: 24,
    vat_months: 0,
    tax_invoices_months: 12,
    cash_bank_months: 12,
    goods_months: 12,
    advance_reports_months: 12,
    commission_months: 12,
    hr_orders_months: 0,
    assets_snapshot_only: true,
    assets_audit_months: 12,
    orders_active_only: true,
  },
  medium_business: {
    payroll_months: 24,
    fifo_months: 36,
    vat_months: 0,
    tax_invoices_months: 24,
    cash_bank_months: 12,
    goods_months: 12,
    advance_reports_months: 12,
    commission_months: 12,
    hr_orders_months: 0,
    assets_snapshot_only: true,
    assets_audit_months: 12,
    orders_active_only: true,
  },
  enterprise: {
    payroll_months: 24,
    fifo_months: 60,
    vat_months: 12,
    tax_invoices_months: 24,
    cash_bank_months: 12,
    goods_months: 12,
    advance_reports_months: 12,
    commission_months: 12,
    hr_orders_months: 0,
    assets_snapshot_only: true,
    assets_audit_months: 12,
    orders_active_only: true,
  },
};

export const migrationRuns = {
  list: (params?: { organization_id?: string }) =>
    api
      .get<MigrationRunListItem[]>('/migration/runs', { params })
      .then((r) => r.data),

  get: (id: string) =>
    api.get<MigrationRun>(`/migration/runs/${id}`).then((r) => r.data),

  create: (payload: MigrationRunCreate) =>
    api.post<MigrationRun>('/migration/runs', payload).then((r) => r.data),

  update: (id: string, payload: MigrationRunUpdate) =>
    api
      .patch<MigrationRun>(`/migration/runs/${id}`, payload)
      .then((r) => r.data),

  remove: (id: string) =>
    api.delete(`/migration/runs/${id}`).then(() => undefined),

  start: (id: string) =>
    api.post<MigrationRun>(`/migration/runs/${id}/start`).then((r) => r.data),

  // Block B Phase 3 (2026-06-01): запустити bulk-POST мігрованих документів.
  // Викликається після того як 1С обробка завершила POST /migration/import
  // (статус run = IMPORTING). Виконується синхронно у backend ~10-30 хв.
  bulkPost: (id: string) =>
    api.post<MigrationRun>(`/migration/runs/${id}/bulk-post`).then((r) => r.data),

  // Block B Phase 4 (2026-06-01): reverse export назад у 1С формат.
  // Закон 5 — 30-денне вікно. Повертає .zip пакет як binary.
  // Phase-3.6 (2026-06-01): optional target_format — для assert Закон 5.
  // Якщо передаєш — має співпадати з migration_run.source_config.
  reverseExport: (id: string, targetFormat?: SourceConfig) =>
    api.post(
      `/migration/runs/${id}/reverse-export`
        + (targetFormat ? `?target_format=${targetFormat}` : ''),
      undefined,
      { responseType: 'blob' },
    ).then((r) => r.data as Blob),

  // Block C Порція 2 (2026-06-01): окремий convenience endpoint для
  // reconciliation report. 204 No Content якщо звіт ще не обчислений.
  reconciliation: (id: string): Promise<ReconciliationReport | null> =>
    api.get<ReconciliationReport>(`/migration/runs/${id}/reconciliation`)
      .then((r) => r.status === 204 ? null : r.data)
      .catch((err) => {
        if (err?.response?.status === 204) return null;
        throw err;
      }),

  // Block C Порція 3 (2026-06-01): override mechanism.
  // Бухгалтер «приймає 1С» на finding → backend створює запис що
  // recalc-сервіси (vacation/sick) будуть використовувати замість обчисленого.
  createOverride: (id: string, payload: MigrationOverrideCreate) =>
    api.post<MigrationOverride>(
      `/migration/runs/${id}/overrides`, payload,
    ).then((r) => r.data),
  listOverrides: (id: string) =>
    api.get<MigrationOverride[]>(`/migration/runs/${id}/overrides`)
      .then((r) => r.data),
};

// Block C Порція 3: типи overrides.
export interface MigrationOverrideCreate {
  target_table: string;  // 'doc.vacation_calculations' / 'doc.sick_leave_calculations'
  target_id: string | null;
  field_name: string;  // 'holiday_kd' / 'average_daily'
  expected_value?: unknown;  // наше обчислене (для audit)
  override_value: unknown;  // 1С/BAS значення
  reason?: string;
}

export interface MigrationOverride extends MigrationOverrideCreate {
  id: string;
  migration_run_id: string;
  applied_by: string;
  applied_at: string;
}
